import { useMemo, useState } from "react";
import { Link } from "wouter";
import { useSession } from "../auth/session";
import { Card, EmptyState, Field, PageHeader, PageShell } from "../company-ui";
import { canOpen, NAV_ITEMS, titleOf } from "../shell/nav";
import { Article } from "./Article";
import { ARTICLE_BY_ID, categoryLabel, searchHelp } from "./content";
import { CATEGORIES } from "./parse";

/** The help center (ALN-53, mockup 14-help-center): every article in docs/help, by category, with search. */
export function HelpCenterPage() {
  const [q, setQ] = useState(() => new URLSearchParams(window.location.search).get("q") ?? "");
  const hits = useMemo(() => searchHelp(q), [q]);
  return (
    <PageShell>
      <PageHeader
        title="Help center"
        subtitle={<>How each screen works, how the costing works, and the rules behind it. Press <kbd>?</kbd> on any page for help with that page, or select a <span className="tip tip--demo" aria-hidden="true">i</span><span className="sr">(i)</span> next to a term.</>}
      />
      <div className="stackgap">
        <Card>
          <div className="card__c">
            <Field label="Search help">
              <input className="input" type="search" value={q} placeholder="For example: idle capacity, vintage, reversal" onChange={e => setQ(e.target.value)} />
            </Field>
          </div>
        </Card>
        <div className="sr" role="status">{q.trim() ? `${hits.length} ${hits.length === 1 ? "article" : "articles"} found` : ""}</div>
        {hits.length === 0 && (
          <Card><EmptyState title={`No help matches "${q.trim()}"`} description="Try a shorter word, or browse the topics." /></Card>
        )}
        {CATEGORIES.map(([key, label]) => {
          const list = hits.filter(a => a.category === key);
          if (!list.length) return null;
          return (
            <section key={key} className="stackgap stackgap--sm" aria-labelledby={`help-cat-${key}`}>
              <h2 className="subhead" id={`help-cat-${key}`}>{label}</h2>
              <div className="helpgrid">
                {list.map(a => (
                  <Link key={a.id} href={`/help/${a.id}`} className="card helpcard">
                    <b>{a.title}</b>
                    <span className="small muted">{a.lead}</span>
                  </Link>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </PageShell>
  );
}

/** One article (mockup 15-help-article), with a way to the screen it explains. */
export function HelpArticlePage({ id }: { id: string }) {
  const { data } = useSession();
  const article = ARTICLE_BY_ID[id];
  if (!article) {
    return (
      <PageShell>
        <PageHeader title="Help article not found" breadcrumbs={[{ label: "Help center", href: "/help" }]} />
        <Card><EmptyState title="There's no help article at this address" action={<Link className="btn" href="/help">All help topics</Link>} /></Card>
      </PageShell>
    );
  }
  const page = article.route ? NAV_ITEMS.find(i => i.path === `/${article.route}`) : undefined;
  const reachable = page && canOpen(page, data);
  return (
    <PageShell>
      <PageHeader
        title={article.title}
        breadcrumbs={[{ label: "Help center", href: "/help" }, { label: categoryLabel(article.category) }]}
        actions={reachable ? <Link className="btn" href={page.path}>Go to {page.tab ? "Compliance" : titleOf(page)}</Link> : undefined}
      />
      <Card>
        <div className="card__c">
          <Article article={article} />
        </div>
      </Card>
    </PageShell>
  );
}
